import { TokenRefreshTimeoutError, InvalidTokenError } from './HostMessageControllerErrors';
import MessageController from './MessageController';
import type { MessageEventData, EventDataCallback } from './Embeddings.types';
import { consumedMessages, emittedMessages } from './messages';

export default class HostMessageController extends MessageController {
  sendInit() {
    return this._send(emittedMessages.init.type);
  }

  sendUrlChange(payload: { url: string | undefined; redirectedFrom?: string | undefined }) {
    return this._send(emittedMessages.urlChange.type, payload);
  }

  sendTokenInvalid() {
    return this._send(emittedMessages.tokenInvalid.type);
  }

  onMessage(cb: EventDataCallback) {
    return this._on(() => true, cb);
  }

  onRouterPop(cb: EventDataCallback) {
    return this._on(consumedMessages.routerPop.type, cb);
  }

  onRouterPush(cb: EventDataCallback) {
    return this._on(consumedMessages.routerPush.type, cb);
  }

  onUpdateToken(cb: EventDataCallback) {
    return this._on(consumedMessages.updateToken.type, cb);
  }

  requestNewToken(timeoutMs = 10000) {
    return new Promise((resolve, reject) => {
      let newToken: string | undefined;

      // setup the event listener for a new token first to avoid race conditions
      const off = this.onUpdateToken((event: MessageEventData | unknown) => {
        const payload =
          typeof event === 'object' && event !== null && 'payload' in event
            ? (event as { payload: { token?: string } }).payload
            : undefined;
        const token = payload?.token;
        const isValid = token !== undefined && this._isValidToken(token);
        if (isValid) {
          newToken = token;
          off();
          resolve(newToken);
        } else {
          reject(new InvalidTokenError('Received invalid token'));
        }
      });

      // clear event listener and reject after timeout
      setTimeout(() => {
        off(); // un-listen after timeout is complete

        if (!newToken)
          return reject(
            new TokenRefreshTimeoutError(
              `Token refresh timed out after ${Math.floor(timeoutMs / 1000)} seconds`
            )
          );
        return resolve(newToken);
      }, timeoutMs);

      // notify the host that the token is invalid
      this.sendTokenInvalid();
    });
  }

  // class utils
  _isValidToken(token: string) {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(token);
  }
}
